import { setTimeout } from 'timers';

import React, { useEffect, useState } from 'react';

import { Button, Tooltip, Typography, useMediaQuery } from '@mui/material';
import copyToClipboard from 'copy-to-clipboard';
import { useRouter } from 'next/router';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import QRCode from 'react-qr-code';

import { lockFileAPI } from '@/api/lockFile';
import Wrapper from '@/components/BodyLayout/Wrapper';
import { PrimaryButton } from '@/components/Button/PrimaryButton';
import { QrPopup } from '@/components/Popup/QrPopup';
import SaveTokenScreen from '@/components/UseNftAndToken/SaveTokenScreen';
import SelectTokenScreen from '@/components/UseNftAndToken/SelectTokenScreen';
import { TooltipTitles } from '@/constants/tooltipTitles';
import useComponentVisible from '@/hooks/useComponentVisible';
import useQrVisible from '@/hooks/useQrVisible';
import errorMessages from '@/utils/errorMessages.json';
import { sendNotifcation } from '@/utils/notification';
import { GetFileResponse } from 'interfaces/ApiType/apiType';
import Token from 'interfaces/token';
import BackBtnLogo from 'public/assets/back.svg';
import CopyIcon from 'public/assets/copy-white.svg';

import { isBrowser } from '../../constants';
import FinalUpload from './FinalUpload';
import PreviewImg from './PreviewImg';
import TokenCreationV2 from './TokenCreation/TokenCreationV2';

interface ShareawayProps {
  fileResponse: GetFileResponse;
  setErrorPage: Function;
}

function SharingAccess({
  fileResponse,
  setSuccessfulAirdrop,
  setSettingVisibility,
  setErrorPage,
}: {
  fileResponse: GetFileResponse;
  setSuccessfulAirdrop: Function;
  setSettingVisibility: Function;
  setErrorPage: Function;
}) {
  const isMediaMax1025px = useMediaQuery('(max-width : 1025px)');
  const [createTokenWithAddress, setCreateTokenWithAddress] =
    useState<boolean>(false);
  const [tokenAmount, setTokenAmount] = useState<any>(
    fileResponse?.data?.token?.gateBalance
  );
  const {
    ref: SelectTokenDiv,
    isComponentVisible: isSelectTokenScreenActive,
    setIsComponentVisible: setSelectTokenScreenActive,
  } = useComponentVisible(false);
  const {
    ref: SaveTokenDiv,
    isComponentVisible: isSaveTokenScreenActive,
    setIsComponentVisible: setSaveTokenScreenActive,
  } = useComponentVisible(false);

  const [userNetworkName, setUserNetworkName] = useState('Ethereum');
  const [token, setToken] = useState<Token>();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const router = useRouter();
  const lockFile = async () => {
    try {
      const recaptchaToken = await executeRecaptcha('lock');
      const resp = await lockFileAPI(
        fileResponse.data.uuid,
        token.contractAddress,
        token.tokenType,
        tokenAmount,
        token.name,
        token.image,
        recaptchaToken
      );
      if (resp.status >= 200 && resp.status < 300) {
        sendNotifcation('File Locked!', '', 'warning');
        setSuccessfulAirdrop(true);
        setSaveTokenScreenActive(false);
      } else if (resp.status >= 500) {
        setErrorPage(true);
        setSaveTokenScreenActive(false);
      }
    } catch (err: any) {
      setSaveTokenScreenActive(false);
      if (err.response?.status >= 500) {
        setErrorPage(true);
        return;
      }
      if (err.response?.status === 403) {
        sendNotifcation(errorMessages[403], '', 'danger');
        return;
      }
      if (err.response?.status === 401) {
        sendNotifcation(errorMessages[401], '', 'danger');
        return;
      }
      sendNotifcation('An error occured!', '', 'danger');
    }
  };

  const selectToken = (value: any) => {
    const data: Token = {
      name: value.symbol,
      image: value.thumbnail,
      contractAddress: value.contractAddress,
      tokenType: 'erc20',
      gateBalance: 1,
      chain: value.chain,
      type: 'token',
    };
    setToken(data);
    if (!tokenAmount) setTokenAmount(1);
    setSelectTokenScreenActive(false);
    setSaveTokenScreenActive(true);
  };

  const selectNft = (value: any) => {
    const data: Token = {
      name: value.symbol,
      image: value.image,
      contractAddress: value.contractAddress,
      tokenType: 'erc721',
      gateBalance: 1,
      chain: value.chain,
      type: 'nft',
    };
    setToken(data);
    if (!tokenAmount) setTokenAmount(1);
    setSelectTokenScreenActive(false);
    setSaveTokenScreenActive(true);
  };

  return (
    <div
      className={`m-auto bg-white rounded-lg py-4  min-w-fit text-center flex flex-wrap relative
      justify-between ${
        isMediaMax1025px ? 'px-8 max-w-[500px]' : 'w-[50%] px-16 '
      } `}
    >
      <div
        className="mb-4 p-1 bg-yellow h-full rounded-full cursor-pointer"
        onClick={() => {
          if (router.query.setAccess === 'true') {
            router.back();
          } else {
            setSettingVisibility(false);
          }
        }}
      >
        <img src={BackBtnLogo.src} alt="Back" className=" h-6" />
      </div>
      <div
        className={` ${
          isMediaMax1025px ? 'w-full' : 'w-6/12'
        }   flex flex-col justify-evenly font-semibold m-auto`}
      >
        <Tooltip
          title={TooltipTitles.manageAccessPermission}
          placement="top-start"
        >
          <div className="text-lg">
            Create Access Rules for Ethereum/EVM addresses
          </div>
        </Tooltip>
        <Tooltip title={TooltipTitles.useNFTAndTokens} placement="top-start">
          <div
            onClick={() => setSelectTokenScreenActive(true)}
            className="px-6 py-2 rounded-md bg-yellow cursor-pointer my-4"
          >
            <span>Use my NFTs & Tokens</span>
          </div>
        </Tooltip>

        <Tooltip title={TooltipTitles.listOfAddresses} placement="top-start">
          <div
            className="px-6 py-2 rounded-md bg-yellow cursor-pointer"
            onClick={() => setCreateTokenWithAddress(true)}
          >
            <span>I have a list of addresses</span>
          </div>
        </Tooltip>
      </div>
      <div className={`m-auto ${isMediaMax1025px ? 'w-full mt-6' : 'w-5/12'}`}>
        <PreviewImg
          fileResponse={fileResponse}
          fromManageAccessPermission={true}
        />
      </div>
      {createTokenWithAddress && (
        <TokenCreationV2
          setSuccessfulAirdrop={setSuccessfulAirdrop}
          setCreateTokenWithAddress={setCreateTokenWithAddress}
        />
      )}
      <div>
        {isSelectTokenScreenActive && (
          <SelectTokenScreen
            selectNft={selectNft}
            isSelectTokenScreenActive={isSelectTokenScreenActive}
            setUserNetworkName={setUserNetworkName}
            userNetworkName={userNetworkName}
            SelectTokenDiv={SelectTokenDiv}
            selectToken={selectToken}
          />
        )}
        {isSaveTokenScreenActive && (
          <SaveTokenScreen
            SaveTokenDiv={SaveTokenDiv}
            isSaveTokenScreenActive={isSaveTokenScreenActive}
            lockFile={lockFile}
            token={token}
            setSelectTokenScreenActive={setSelectTokenScreenActive}
            setSaveTokenScreen={setSaveTokenScreenActive}
            setTokenAmount={setTokenAmount}
            tokenAmount={tokenAmount}
          />
        )}
      </div>
    </div>
  );
}

export default function Shareaway({
  fileResponse,
  setErrorPage,
}: ShareawayProps) {
  const router = useRouter();
  const { fileId, ownerFirstVisit, setAccess } = router.query;
  const currentURL =
    isBrowser && fileResponse
      ? `${window.location.origin}/files/${fileId}`
      : '';
  const [copyURLVal, setURLCopyVal] = useState<string>('Copy');
  const [settingVisibility, setSettingVisibility] = useState<boolean>(false);
  const isMediaMax1025px = useMediaQuery('(max-width : 1025px)');
  // const isMedia640px = useMediaQuery('(max-width : 640px)');
  const [successfulAirdrop, setSuccessfulAirdrop] = useState<boolean>(false);
  const { qrRef, isQrVisible, setIsQrVisible } = useQrVisible(false);

  const QR = () => {
    return (
      <div ref={qrRef} className="relative">
        <div className="flex-col w-full h-full items-center justify-center">
          <div className="flex w-full p-2 my-3 justify-center">
            <div className="border-2 p-2 rounded-md">
              <QRCode
                size={115}
                value={
                  `${process.env.NEXT_PUBLIC_ROOT_URL}/files/${fileId}` ||
                  `${process.env.NEXT_PUBLIC_ROOT_URL}/`
                }
              />
            </div>
          </div>
          <div className="flex w-full p-2 justify-center">
            Scan QR Code from Mobile
          </div>
        </div>
      </div>
    );
  };

  const copyURLAnimation = () => {
    setURLCopyVal('Copied');
    setTimeout(() => {
      setURLCopyVal('Copy');
    }, 500);
  };

  useEffect(() => {
    if (successfulAirdrop && ownerFirstVisit !== 'true')
      router.replace(`/files/${fileId}`);
  }, [successfulAirdrop, ownerFirstVisit, router, fileId]);

  if (successfulAirdrop && ownerFirstVisit === 'true') {
    return <FinalUpload fileResponse={fileResponse} />;
  }

  return (
    <Wrapper>
      {settingVisibility || setAccess === 'true' ? (
        <SharingAccess
          fileResponse={fileResponse}
          setSuccessfulAirdrop={setSuccessfulAirdrop}
          setSettingVisibility={setSettingVisibility}
          setErrorPage={setErrorPage}
        />
      ) : (
        <div
          className={`bg-white rounded-lg py-8 text-center flex m-auto items-center relative
         ${
           isMediaMax1025px ? 'px-8 w-full flex-col' : 'w-[50%] px-16 flex-row'
         } `}
        >
          <div
            className={` h-full ${
              isMediaMax1025px ? 'w-full mb-6' : 'mr-12 w-5/12'
            }`}
          >
            <PreviewImg fileResponse={fileResponse} />
          </div>
          <div
            className={`flex flex-col ${
              isMediaMax1025px ? 'w-full' : ' w-7/12'
            }`}
          >
            <div className="font-bold text-3xl mb-4">Share Away!</div>
            <div className="block font-semibold w-full text-sm mb-4">
              Your file is now encrypted and stored on IPFS. Share the link to
              grant people access <span className="font-bold">OR</span> create
              on-chain access rules to your file.
            </div>
            <div className="w-full flex flex-row justify-start mt-2 mb-1 font-semibold">
              <div
                onClick={() => setIsQrVisible(true)}
                className="cursor-pointer"
              >
                Open QR Code
              </div>
            </div>
            <div className="w-full mb-4 relative flex border rounded-md">
              <input
                readOnly
                defaultValue={currentURL}
                className="p-3 text-gray-500 caret-transparent w-[73%] rounded-md"
              />

              <Button
                variant="text"
                onClick={() => {
                  copyToClipboard(currentURL);
                  copyURLAnimation();
                }}
                className="absolute bg-black normal-case"
                style={{
                  textTransform: 'none',
                  bottom: '6px',
                  right: '10px',
                }}
                disableElevation={true}
              >
                <Typography className="text-white flex ">
                  <img src={CopyIcon.src} alt="Copy" />
                  <span className="ml-2 font-thin">{copyURLVal}</span>
                </Typography>
              </Button>
            </div>
            <div className="font-bold">OR</div>
            <div className="m-auto mt-4">
              <PrimaryButton onClick={() => setSettingVisibility(true)}>
                <span className="px-2 font-semibold">Set Access Rules</span>
              </PrimaryButton>
            </div>
          </div>
          <QrPopup
            isOpen={isQrVisible}
            width={isMediaMax1025px ? '90%' : '370px'}
            margin={isMediaMax1025px ? '5%' : '10% auto'}
            closePopup={() => {}}
          >
            <div className="overflow-auto max-h-full max-w-full box-border">
              {QR()}
            </div>
          </QrPopup>
        </div>
      )}
    </Wrapper>
  );
}
